import { Box, Text } from '@chakra-ui/react'
import type { ReactElement } from 'react'

import { FEATURED_FEEDBACK_LIMIT_PER_TYPE } from '@/utils/constants'
import { pluralize } from '@/utils/helpers'

import useAPIWrapper from '@/client/hooks/useAPIWrapper'
import { RPCRefresh } from '@/client/rpc'

import { useToaster } from '@/components/Toast/useToaster'
import StarWithTooltip from '@/components/UI/StarWithTooltip'

import type { FeedbackType } from '../types'

const FEEDBACK_TYPE_TO_LABEL: Record<FeedbackType, string> = {
  'old-audit': 'peer review',
  audit: 'peer review comment',
  session: 'learner feedback',
  post: 'post feedback',
  self: 'self feedback',
  'learner-self': 'Dialogues self reflection',
  'learner-peer': 'Dialogues peer feedback',
}

const FEEDBACK_TYPE_TO_UNFEATURE_HANDLER: Record<
  FeedbackType,
  (feedbackId: number, params?: { auditRubricQuestionSlug: string | null }) => Promise<void>
> = {
  'old-audit': async feedbackId =>
    RPCRefresh.unfeatureTutorAudit({ tutorAuditId: feedbackId, questionSlug: null }),
  session: async feedbackId =>
    RPCRefresh.unfeatureSessionFeedback({ sessionFeedbackId: feedbackId }),
  post: async feedbackId => RPCRefresh.unfeaturePostFeedback({ postFeedbackId: feedbackId }),
  audit: async (feedbackId, params) => {
    if (params?.auditRubricQuestionSlug == null) {
      throw new Error("Can't unfeature audit without comment key")
    }
    await RPCRefresh.unfeatureTutorAudit({
      tutorAuditId: feedbackId,
      questionSlug: params.auditRubricQuestionSlug,
    })
  },
  self: async () => Promise.resolve(),
  'learner-self': async feedbackId =>
    RPCRefresh.unfeatureLearnerFeedback({ learnerFeedbackId: feedbackId }),
  'learner-peer': async feedbackId =>
    RPCRefresh.unfeatureLearnerFeedback({ learnerFeedbackId: feedbackId }),
}

const FEEDBACK_TYPE_TO_FEATURE_HANDLER: Record<
  FeedbackType,
  (feedbackId: number, params?: { auditRubricQuestionSlug: string | null }) => Promise<void>
> = {
  'old-audit': async feedbackId =>
    RPCRefresh.featureTutorAudit({ tutorAuditId: feedbackId, questionSlug: null }),
  session: async feedbackId => RPCRefresh.featureSessionFeedback({ sessionFeedbackId: feedbackId }),
  post: async feedbackId => RPCRefresh.featurePostFeedback({ postFeedbackId: feedbackId }),
  audit: async (feedbackId, params) => {
    if (params?.auditRubricQuestionSlug == null) {
      throw new Error("Can't feature audit without comment key")
    }
    await RPCRefresh.featureTutorAudit({
      tutorAuditId: feedbackId,
      questionSlug: params.auditRubricQuestionSlug,
    })
  },
  self: async () => Promise.resolve(),
  'learner-self': async feedbackId =>
    RPCRefresh.featureLearnerFeedback({ learnerFeedbackId: feedbackId }),
  'learner-peer': async feedbackId =>
    RPCRefresh.featureLearnerFeedback({ learnerFeedbackId: feedbackId }),
}

export default function FeedbackStar({
  feedbackId,
  auditRubricQuestionSlug = null,
  type,
  featuredCount = 0,
  isFeatured,
  isEmpty,
}: {
  feedbackId: number
  auditRubricQuestionSlug?: string | null
  type: FeedbackType
  featuredCount?: number
  isFeatured: boolean
  isEmpty: boolean
}): ReactElement {
  const toast = useToaster()

  const [handleUnfeature, isUnfeaturing] = useAPIWrapper(
    async () => {
      await FEEDBACK_TYPE_TO_UNFEATURE_HANDLER[type](feedbackId, {
        auditRubricQuestionSlug,
      })

      toast({
        title: 'Successfully removed featured feedback',
        status: 'success',
      })
    },
    [feedbackId, toast, type, auditRubricQuestionSlug],
    'Unfeature Feedback'
  )

  const [handleFeature, isFeaturing] = useAPIWrapper(
    async () => {
      await FEEDBACK_TYPE_TO_FEATURE_HANDLER[type](feedbackId, {
        auditRubricQuestionSlug,
      })
      toast({
        title: 'This feedback has been featured!',
        status: 'success',
      })
    },
    [feedbackId, toast, type, auditRubricQuestionSlug],
    'Feature Feedback'
  )

  const state = isFeatured
    ? 'featured'
    : isEmpty
      ? 'empty'
      : featuredCount === FEATURED_FEEDBACK_LIMIT_PER_TYPE[type]
        ? 'at-limit'
        : 'can-feature'

  const label = FEEDBACK_TYPE_TO_LABEL[type]

  if (state === 'featured') {
    return (
      <StarWithTooltip
        tooltipContent={`Stop featuring this ${label}?`}
        color="primary.solid"
        onClick={handleUnfeature}
        solid
        disabled={isUnfeaturing}
      />
    )
  }

  if (state === 'empty') {
    return (
      <StarWithTooltip tooltipContent={`Can't feature empty feedback.`} color="gray.emphasized" />
    )
  }

  if (state === 'at-limit') {
    return (
      <StarWithTooltip
        tooltipContent={`You've reached your limit of ${FEATURED_FEEDBACK_LIMIT_PER_TYPE[type]} featured ${pluralize(
          FEATURED_FEEDBACK_LIMIT_PER_TYPE[type],
          label
        )}.`}
        color="gray.emphasized"
      />
    )
  }

  return (
    <StarWithTooltip
      disabled={isFeaturing}
      tooltipContent={
        <Box>
          <Text color="fg.inverted">
            Feature this feedback on your public profile and Schoolhouse Portfolio?
          </Text>
          <Text color="fg.inverted" pt={4}>
            You can feature{' '}
            {pluralize(FEATURED_FEEDBACK_LIMIT_PER_TYPE[type] - featuredCount, `more ${label}`)}.
          </Text>
        </Box>
      }
      onClick={handleFeature}
      color="fg"
    />
  )
}
